import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  Suspense,
} from "react";
import styled from "styled-components";

// Whether what is drawn is in view, which the children of a LazyInView read
// to stop asking for data that nobody is looking at. What is not inside one
// of them is always in view.
const InViewContext = React.createContext(true);

export const useIsInView = () => useContext(InViewContext);

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  width: 100%;
  font-family: "AllianceNo2-SemiBold", sans-serif;
`;

const useInView = (options) => {
  const [inView, setInView] = useState(false);
  const ref = useRef();
  const rootMargin = options?.rootMargin || "200px 0px";

  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting === true),
      { rootMargin },
    );

    const currentRef = ref.current;
    currentRef && observer.observe(currentRef);

    return () => {
      currentRef && observer.unobserve(currentRef);
    };
  }, [rootMargin]);

  return [ref, inView];
};

const LazyInView = ({
  children,
  fallback = <LoadingWrapper>Loading...</LoadingWrapper>,
  rootMargin,
  className = undefined,
}) => {
  const [ref, inView] = useInView({ rootMargin });
  // What has been in view once stays mounted, so that its data and what is
  // drawn of it are kept. It is told when it goes out of view instead, which
  // is what stops it from asking for more while nobody looks at it.
  const wasInView = useRef(false);

  wasInView.current = wasInView.current || inView;

  return (
    <div ref={ref} className={className}>
      {wasInView.current ? (
        <InViewContext.Provider value={inView}>
          <Suspense fallback={fallback}>{children}</Suspense>
        </InViewContext.Provider>
      ) : (
        fallback
      )}
    </div>
  );
};

export default LazyInView;
